import { FC } from 'react'

import { Box } from '@sequencehq/core-components'

import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ConditionalPropertyValueFormatter } from 'Automations/nodeDefinitions/conditions/conditions.types'
import { readPreviousApprovalValue } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  PREVIOUS_APPROVAL_LABEL,
  REVIEW_NAME_TEST_ID
} from './previousApproval.constants'

/**
 * Names the review step the row compares against, ahead of the subject, so a
 * switch holding rows for two steps says which is which. The name is bold, so
 * it reads as the subject rather than part of the clause:
 *
 *   **Deal desk sign-off** previous approval raw text is equal
 */
export const formatPreviousApprovalSubject: ConditionalPropertyValueFormatter =
  ({ value, nodes }) => (
    <PreviousApprovalSubject
      reviewNodeId={readPreviousApprovalValue(value).reviewNodeId}
      nodes={nodes}
    />
  )

const SUBJECT = 'previous approval'

/**
 * Reads the step's name off the node itself rather than the editor's atoms, so
 * an execution's history — which renders against a saved version's nodes —
 * names the step too.
 *
 * Falls back to the subject alone when the step can't be named: a row pointing
 * at a step since removed, or one read without its workflow.
 */
const PreviousApprovalSubject: FC<{
  reviewNodeId: string | undefined
  nodes: AutomationsPresentationV1Node[]
}> = ({ reviewNodeId, nodes }) => {
  const reviewNode = reviewNodeId
    ? nodes.find(node => node.id === reviewNodeId)
    : undefined
  const name =
    reviewNode && 'name' in reviewNode.data && reviewNode.data.name
      ? String(reviewNode.data.name)
      : ''

  if (!name) return <>{PREVIOUS_APPROVAL_LABEL}</>
  return (
    <>
      {/* Weight only — the clause is rendered at different sizes on the canvas
          and in the execution summary, and both should keep their own. */}
      <Box as="span" data-testid={REVIEW_NAME_TEST_ID} fontWeight="600">
        {name}
      </Box>{' '}
      {SUBJECT}
    </>
  )
}
